import { z } from 'zod';
import {
  MAX_COST_CENTER_SPLIT_ROWS,
  ModalSessionFlightPolicyViolationSchema,
} from '@voll-intelligence/domain';

import { ChatUiMoneySchema } from '../../shared/schema/money_schema.js';
import { ChatUiFlightSegmentSchema } from '../../shared/schema/flight_segment_schema.js';
import { ChatUiBusStopSchema } from '../../bus/bus_offers/bus_offers_schema.js';
import { chatUiBase } from '../../shared/schema/base.js';
import { TravelerRefSchema } from '../../shared/schema/traveler_ref.js';
import { HotelAmenitySchema } from '../../hotel/shared/hotel_amenity_schema.js';

/**
 * Unified checkout contract. Card and drawer share one product-agnostic core;
 * product modules contribute item detail, field manifests, and submit mapping.
 */

// ── Shared primitives ────────────────────────────────────────────────────────

/** A picked corporate option, carrying id + label. Maps 1:1 to the upstream
 *  `RentalNamedRef` — the client already holds the name from `getBookingForm`,
 *  so it is ECHOED on submit (no server id→name refetch at reserve time). */
export const NamedRefSchema = z.object({ id: z.string().min(1), name: z.string() });
export type NamedRef = z.infer<typeof NamedRefSchema>;

/** One corporate allocation row. A traveler carries up to
 *  `MAX_COST_CENTER_SPLIT_ROWS` rows (rateio, #6697); with more than one, each row
 *  carries its `percent` and the BFF checks the split (`costCenterSplitIssue`). */
export const AllocationSchema = z.object({
  company: NamedRefSchema,
  costCenter: NamedRefSchema,
  // Only present when the client's enableProjectField is on (company-scoped).
  project: NamedRefSchema.optional(),
  // Absent on a lone row (it counts as 100); required on every row of a split.
  percent: z.number().min(0).max(100).optional(),
});
export type Allocation = z.infer<typeof AllocationSchema>;

/** The rows of one traveler's allocation: structure only — the sum and the
 *  repeated cost centers are the BFF's (`costCenterSplitIssue`). */
const AllocationListSchema = z.array(AllocationSchema).min(1).max(MAX_COST_CENTER_SPLIT_ROWS);

/** Editable traveler document. Lowercase discriminant (mirrors the rental shape,
 *  not flight's uppercase `PASSPORT`). `cpf` only travels when the profile has NO
 *  CPF on file (#5809), in digits: a CPF on file stays backend-resolved by
 *  traveler id and is never resent. */
export const CheckoutDocumentSchema = z.object({
  type: z.enum(['cpf', 'passport', 'dni']),
  value: z.string().min(1),
  issuingCountry: z.string().optional(),
  expirationDate: z.string().optional(),
});
export type CheckoutDocument = z.infer<typeof CheckoutDocumentSchema>;

/** Canonical traveler edits for `product_checkout` submissions (rental + hotel +
 *  flight + bus + mobility). "Wire carries the superset": um campo aqui NÃO
 *  garante que o produto o consuma, e cada consumidor decide o que lê.
 *
 *  Auditado por produto (Ago/2026; mobility revisto em Out/2026): voo lê tudo
 *  (`buildOneTraveler` → `flight/ops/booking.ts`, que tem campo para cada um,
 *  `nationality` incluída); rental e bus leem `edits` nos seus nós de checkout;
 *  mobility lê só o `phone`, que vira o `phoneNumber` do agendamento
 *  (`domains/mobility/logic/create_schedule_input.ts`); **hotel não lê `edits`
 *  em NENHUM caminho** (nem a bancada `hotel/booking.ts`, nem
 *  `domains/hotel/graph/nodes/checkout.ts`) — ver o aviso em `HOTEL_FIELDS`. Um
 *  campo pedido na tela cujo produto não lê `edits` é preenchido, validado e
 *  descartado sem erro — por isso o hotel não pede identidade nenhuma.
 *
 *  CADASTRO (#5279): o `POST /business/checkout` do app-integration grava no
 *  perfil (`businessUpdateUserProfile`) o que mudou nos `edits` do PRÓPRIO
 *  viajante logado — nascimento, nacionalidade, nome de embarque, documento e o
 *  telefone e o CPF de quem não tem um em ficha; `gender` não (#2639). Os `edits` de um
 *  colega valem só para a reserva daquele submit (#5329). Fail-soft: a reserva
 *  segue mesmo se a gravação falhar. */
export const CheckoutTravelerEditsSchema = z.object({
  birthdate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  nationality: z.object({ code: z.string().optional(), name: z.string().optional() }).optional(),
  document: CheckoutDocumentSchema.optional(),
  // general — a manifest may require it; requiredness is server-driven
  gender: z.enum(['F', 'M']).optional(),
  // flight-only identity superset
  boardingName: z.string().optional(), // nome como no bilhete
  boardingSurname: z.string().optional(), // sobrenome como no bilhete
  phone: z.string().optional(), // flight: required (enforced by canSubmit)
  countryCode: z.string().optional(), // flight: dialing code paired with phone
});
export type CheckoutTravelerEdits = z.infer<typeof CheckoutTravelerEditsSchema>;

/** Bloco por-viajante (flight/hotel multi-traveler): alocação + edits daquele viajante. */
export const CheckoutTravelerBlockSchema = z.object({
  travelerId: z.string().min(1),
  // Opcional desde o multi de HOTEL: o rateio corporativo é do bloco do
  // REQUESTER e o mapper do BFF descarta as alocações dos acompanhantes —
  // exigi-las tornava o checkout impossível para um colega sem catálogo
  // próprio. Presente ⇒ ainda exatamente uma (afrouxamento aditivo; o
  // servidor revalida a do requester). Mais de uma linha só com a flag do
  // rateio (#6697), conferida no BFF.
  allocations: AllocationListSchema.optional(),
  edits: CheckoutTravelerEditsSchema.optional(),
});
export type CheckoutTravelerBlock = z.infer<typeof CheckoutTravelerBlockSchema>;

/** UI-only traveler draft. CPF is selectable in the document toggle so the user
 *  can return to the backend-resolved CPF path, but it is stripped before the
 *  `product-checkout-submit` payload is built. */
export type CheckoutTravelerDraft = Omit<CheckoutTravelerEdits, 'document'> & {
  document?: CheckoutTravelerEdits['document'] | { type: 'cpf'; value: string };
};

// ── Item detail (per productType) ───────────────────────────────────────────

/** Rental's item detail — the fields `rental_item_detail.tsx` renders (compact
 *  card + expanded drawer panel). Mirrors `ChatUiRentalOfferSchema` minus
 *  `offerId`/`price` (those live on `CheckoutItem` itself). `pickupAt`/
 *  `dropoffAt` are optional and, as of this migration, ALWAYS absent — the v2
 *  agents `RentalOffer` FSM shape does not carry search dates yet (a pre-existing
 *  gap; not this migration's scope to fix). */
export const RentalItemDetailSchema = z.object({
  vehicleName: z.string().optional(),
  category: z.string(),
  rentalCompany: z.string(),
  /** Código da locadora (`rental.id`, ex.: "MO") — a linha de fidelidade casa o
   *  programa do perfil por ele (#6159). Aditivo: card antigo sem o campo fica
   *  sem a linha. */
  companyCode: z.string().optional(),
  thumbUrl: z.string().optional(),
  categoryIconUrl: z.string().optional(),
  companyLogoUrl: z.string().optional(),
  transmission: z.enum(['AUTOMATIC', 'MANUAL']),
  airConditioning: z.boolean(),
  pickupAt: z.string().optional(),
  dropoffAt: z.string().optional(),
  pickUpAgency: z.string().optional(),
  pickUpAddress: z.string().optional(),
  dropOffAgency: z.string().optional(),
  dropOffAddress: z.string().optional(),
  withinPolicy: z.boolean(),
  days: z.number().int().min(1).optional(),
});
export type RentalItemDetail = z.infer<typeof RentalItemDetailSchema>;

const RentalCheckoutItemSchema = z.object({
  productType: z.literal('rental'),
  offerId: z.string().min(1),
  price: ChatUiMoneySchema,
  detail: RentalItemDetailSchema,
});

/** Hotel's item detail — the fields `hotel_item_detail.tsx` renders (compact
 *  card + drawer side panel): property/room/board/dates/nights + the corporate
 *  policy flag. `checkIn`/`checkOut` are ISO dates (YYYY-MM-DD); the drawer reads
 *  `checkIn` as the budget query's `startDate`. */
export const HotelItemDetailSchema = z.object({
  hotelName: z.string().optional(),
  roomName: z.string().optional(),
  /** Nome CRU do fornecedor para a tarifa escolhida — a string contratual que
   *  o viajante confirma, ao lado do rótulo canônico (spec 2026-08-25). */
  roomSource: z.string().optional(),
  board: z.string().optional(),
  address: z.string().optional(),
  /** O bloco «Hospedagem» do 1click (#5281) — os fatos do hotel ESCOLHIDO, como a
   *  busca os trouxe. Tudo opcional e aditivo: card antigo do histórico sem os
   *  campos segue parseando e cai na legenda da estadia. */
  city: z.string().optional(),
  /** Nota 0–5, a mesma régua de `HotelOfferSchema.rating` (0 = sem avaliação). */
  rating: z.number().min(0).max(10).optional(),
  reviewCount: z.number().int().nonnegative().optional(),
  /** As 3 primeiras comodidades da propriedade, na ordem do fornecedor. */
  amenities: z.array(HotelAmenitySchema).max(3).optional(),
  /** Distância até a âncora da busca (`HotelOfferSchema.distanceKm`), e o nome
   *  dessa âncora quando o agente o sabe — nem sempre é o escritório. */
  distanceKm: z.number().nonnegative().optional(),
  distanceAnchor: z.string().min(1).max(60).optional(),
  thumbUrl: z.string().optional(),
  checkIn: z.string().optional(),
  checkOut: z.string().optional(),
  nights: z.number().int().min(1).optional(),
  /** Hóspedes por quarto, da busca: com ela a tela sabe quem é titular de cada
   *  quarto e pede o rateio de cada titular (#5522, `hotelRooms`). Aditivo: item
   *  antigo sem o campo segue com o rateio de quem reserva. */
  guestsPerRoom: z.number().int().min(1).max(4).optional(),
  refundable: z.boolean().optional(),
  /** Até quando cancelar não custa nada (ISO), como a COTAÇÃO confirmou — não
   *  como a busca prometeu. Aditivo: item antigo sem o campo cai no `refundable`. */
  freeCancellationUntil: z.iso.date().optional(),
  breakfast: z.boolean().optional(),
  withinPolicy: z.boolean(),
});
export type HotelItemDetail = z.infer<typeof HotelItemDetailSchema>;

const HotelCheckoutItemSchema = z.object({
  productType: z.literal('hotel'),
  offerId: z.string().min(1),
  price: ChatUiMoneySchema,
  detail: HotelItemDetailSchema,
});

/** Flight's item detail — the fields the flight card (`flight/checkout_legs.tsx`)
 *  and screen summary (`flight/summary_legs.tsx`) render: ida[/volta] segments + cabin + a display
 *  passenger string + the corporate policy flag. Mirrors the legacy
 *  `booking_approval` flight summary. */
export const FlightItemDetailSchema = z.object({
  outbound: ChatUiFlightSegmentSchema,
  return: ChatUiFlightSegmentSchema.optional(),
  /** A tarifa da ida — a do itinerário inteiro quando ida e volta são uma
   *  tarifa só. */
  fareClass: z.string().optional(),
  /** A tarifa da volta vendida à parte (#3844). Ausente = a mesma de
   *  `fareClass` (cards antigos do histórico seguem assim). */
  returnFareClass: z.string().optional(),
  /** As companhias de cada sentido (IATA, únicas, na ordem das pernas) — a
   *  linha de fidelidade casa cada uma com o programa do perfil (#5340). Aditivo:
   *  ausente (card antigo, perna sem identidade), cai no `carrier` do segmento. */
  carriers: z
    .object({
      outbound: z.array(z.string().min(1)).min(1),
      return: z.array(z.string().min(1)).min(1).optional(),
    })
    .optional(),
  passengers: z.string().optional(),
  withinPolicy: z.boolean(),
  /** As regras que reprovaram a tarifa (#5262) — o porquê da justificativa
   *  obrigatória. Só com `withinPolicy: false`; ausente = o aviso genérico. */
  policyViolations: z.array(ModalSessionFlightPolicyViolationSchema).optional(),
});
export type FlightItemDetail = z.infer<typeof FlightItemDetailSchema>;

const FlightCheckoutItemSchema = z.object({
  productType: z.literal('flight'),
  offerId: z.string().min(1),
  price: ChatUiMoneySchema,
  detail: FlightItemDetailSchema,
});

/** Bus's item detail — the fields `bus/item_detail.tsx` renders (compact card +
 *  expanded drawer panel): a leg-based itinerary like flight, but keyed on bus
 *  stops (rodoviárias) instead of IATA segments. Mirrors `ChatUiBusOffer` minus
 *  `offerId`/`price` (those live on `CheckoutItem` itself), reusing the shared
 *  `ChatUiBusStop` shape rather than inventing a new one. `departure.dateTime`
 *  seeds the budget query's `startDate` in the drawer. */
export const BusItemDetailSchema = z.object({
  departure: ChatUiBusStopSchema,
  arrival: ChatUiBusStopSchema,
  // Duração total da viagem em minutos (fonte autoritativa do provider).
  durationMinutes: z.number().int().min(0).optional(),
  // Empresa/viação operadora (ex.: "Cometa").
  carrierName: z.string().optional(),
  // Logo da viação (`travelCompany.logo.url` da busca) — o lockup do cabeçalho
  // do trecho no card 1click, no lugar do nome. Ausente → o nome escrito.
  carrierLogoUrl: z.string().min(1).optional(),
  // Descrição da classe de poltrona (ex.: "Leito", "Executivo").
  seatClass: z.string().optional(),
  // Número de conexões (0 = direto). Ausente → tratado como direto.
  connections: z.number().int().min(0).optional(),
  withinPolicy: z.boolean(),
  // A perna no carrinho de ida e volta (rótulo da linha de preço e do bloco).
  // Ausente → item único.
  leg: z.enum(['outbound', 'return']).optional(),
  // As poltronas escolhidas por trecho, na ordem da viagem; `[]` = seguir sem
  // assento (a equipe VOLL escolhe).
  seats: z.array(z.array(z.string().min(1))).optional(),
});
export type BusItemDetail = z.infer<typeof BusItemDetailSchema>;

const BusCheckoutItemSchema = z.object({
  productType: z.literal('bus'),
  offerId: z.string().min(1),
  price: ChatUiMoneySchema,
  detail: BusItemDetailSchema,
});

/** Mobility's item detail — the fields `mobility/item_detail.tsx` renders
 *  (compact card + expanded drawer panel): a cooperative ride (outward[/return],
 *  each leg with its ordered stops) or a car-with-driver daily package (pickup
 *  only — the car stays at the traveler's disposal, so no destination —,
 *  days/hours/km or free km, vehicle category + bilingual/overnight extras),
 *  the flight the ride follows + the corporate policy flag. Mirrors
 *  `ChatUiMobilityOffer`'s section discriminant (`cooperative`/`car_driver`) but
 *  scoped to the finalized checkout item, not the offer list. */
export const MobilityItemDetailSchema = z.object({
  service: z.enum(['cooperative', 'car_driver']),
  supplierName: z.string().min(1),
  outward: z.object({
    from: z.string().min(1),
    // Absent for the car with driver: no destination.
    to: z.string().min(1).optional(),
    scheduleDate: z.string().min(1),
    // Stop labels, in route order.
    stops: z.array(z.string().min(1)).optional(),
  }),
  return: z
    .object({
      from: z.string().min(1),
      to: z.string().min(1),
      scheduleDate: z.string().min(1),
      stops: z.array(z.string().min(1)).optional(),
    })
    .optional(),
  companions: z.number().int().min(0).max(3).optional(),
  days: z.number().int().positive().optional(),
  hours: z.number().int().positive().optional(),
  km: z.number().nonnegative().optional(),
  // The chosen tariff has a free-km franchise (shown instead of `km`).
  freeKm: z.boolean().optional(),
  // Car-with-driver vehicle category ("Sedan executivo").
  vehicle: z.string().min(1).optional(),
  bilingual: z.boolean().optional(),
  overnightDays: z.number().int().nonnegative().optional(),
  // The flight the ride follows — the text the cooperative reads in the ride's
  // preference, so always in pt-BR whatever the conversation's language
  // ("Voo LA3456 (LATAM) · pouso 15:45").
  flightNote: z.string().min(1).optional(),
  // The estimated price is the direct route's: the cooperative search does not
  // price the stops.
  excludesStops: z.boolean().optional(),
  withinPolicy: z.boolean(),
  // #6699: the passenger contact the driver calls, typed in the ride details —
  // valid for this ride only, never written to the traveler's profile. `name`
  // absent when the requester is the traveler.
  contact: z
    .object({
      name: z.string().trim().min(1).max(120).optional(),
      phone: z.string().trim().min(1).max(40),
    })
    .optional(),
  // #6699: the route length the distance policy judged (km), for the
  // out-of-policy mark on the total; `distanceLimitKm` only when known.
  distanceKm: z.number().nonnegative().optional(),
  distanceLimitKm: z.number().positive().optional(),
});
export type MobilityItemDetail = z.infer<typeof MobilityItemDetailSchema>;

const MobilityCheckoutItemSchema = z.object({
  productType: z.literal('mobility'),
  offerId: z.string().min(1),
  price: ChatUiMoneySchema,
  detail: MobilityItemDetailSchema,
});

/** Discriminated by `productType` (rental + hotel + flight + bus + mobility).
 *  New products add a member here + a case in `detail/item_detail.tsx` (YAGNI:
 *  no speculative shape). */
export const CheckoutItemSchema = z.discriminatedUnion('productType', [
  RentalCheckoutItemSchema,
  HotelCheckoutItemSchema,
  FlightCheckoutItemSchema,
  BusCheckoutItemSchema,
  MobilityCheckoutItemSchema,
]);
export type CheckoutItem = z.infer<typeof CheckoutItemSchema>;

// ── Documentos da viagem (voo internacional) ─────────────────────────────────

/** Um requisito da rota vindo do Timatic (ou da lista fixa): o viajante marca
 *  "tenho"; não trava a reserva. `id` = `${país}:${tipo}` (vai no submit). */
export const CheckoutTravelDocItemSchema = z.object({
  id: z.string().min(1).max(40),
  kind: z.enum(['passport', 'visa', 'health']),
  status: z.enum(['required', 'attention', 'ok']),
  title: z.string().trim().min(1).max(120),
  detail: z.string().trim().max(600).optional(),
});
export type CheckoutTravelDocItem = z.infer<typeof CheckoutTravelDocItemSchema>;

export const CheckoutTravelDocsSchema = z.object({
  source: z.enum(['timatic', 'fallback']),
  blocks: z
    .array(
      z.object({
        country: z.string().min(1).max(60),
        applicability: z.enum(['entry', 'transit']),
        items: z.array(CheckoutTravelDocItemSchema).min(1).max(3),
      }),
    )
    .min(1)
    .max(6),
});
export type CheckoutTravelDocs = z.infer<typeof CheckoutTravelDocsSchema>;

/** `GET /trips/:tripId/travel-docs` (app-integration, #6318): a mesma seção na página
 *  da viagem; `null` quando ela não cruza fronteira ou não tem voo vivo. */
export const TripTravelDocsResponseSchema = z.object({
  travelDocs: CheckoutTravelDocsSchema.nullable(),
});
export type TripTravelDocsResponse = z.infer<typeof TripTravelDocsResponseSchema>;

/** Os documentos que o viajante viu e confirmou no checkout, com a fonte e o status de cada um. */
export const CheckoutTravelDocsAckSchema = z.object({
  source: CheckoutTravelDocsSchema.shape.source,
  items: z
    .array(CheckoutTravelDocItemSchema.pick({ id: true, status: true }))
    .min(1)
    .max(18),
});
export type CheckoutTravelDocsAck = z.infer<typeof CheckoutTravelDocsAckSchema>;

// ── Submit payload ───────────────────────────────────────────────────────────

/** The `product-checkout-submit` action's payload — rides OPAQUE inside
 *  `AppChannelConfirmation.selections` on the wire (the domain schema already
 *  treats `selections` as `z.unknown()`). Parsed at the agents checkout readers
 *  against this exact schema. */
export const CheckoutSubmitSchema = z
  .object({
    // Legado single-traveler: o kit não emite mais, mas um submit de bundle
    // antigo ainda chega. Ausente quando `travelers` presente.
    allocations: AllocationListSchema.optional(),
    traveler: CheckoutTravelerEditsSchema.optional(),
    // Um bloco por viajante: quando presente, SUPERSEDE allocations/traveler.
    travelers: z.array(CheckoutTravelerBlockSchema).min(1).optional(),
    policyTravelerId: z.string().min(1).optional(),
    motive: NamedRefSchema,
    justification: NamedRefSchema.optional(),
    observation: z.string().max(300).optional(),
    // Ids dos documentos que o viajante marcou como "tenho" (voo internacional).
    travelDocsChecked: z.array(z.string().min(1).max(40)).max(18).optional(),
    travelDocsAck: CheckoutTravelDocsAckSchema.optional(),
  })
  .refine((s) => Boolean(s.travelers ?? s.allocations), {
    message: 'checkout submit precisa de travelers[] ou allocations',
  });
export type CheckoutSubmit = z.infer<typeof CheckoutSubmitSchema>;

// ── Field manifest (structure only — data/requiredness come from the server) ─

/** Canonical field descriptor union across ALL products; the per-product
 *  manifests (`core/field_manifest.ts`) each pick their subset. `group` drives which
 *  drawer section (`form_host.tsx`) the field renders in — mirrors today's two
 *  edit-toggle cards (traveler / corporate) rather than exploding into N
 *  independently-toggled atoms. */
export const CheckoutFieldSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('allocation'), group: z.literal('corporate') }),
  z.object({
    kind: z.literal('select'),
    group: z.literal('corporate'),
    id: z.enum(['motive', 'justification']),
    labelKey: z.string(),
  }),
  z.object({
    kind: z.literal('observation'),
    group: z.literal('corporate'),
    maxLength: z.number(),
  }),
  z.object({ kind: z.literal('birthdate'), group: z.literal('traveler') }),
  z.object({ kind: z.literal('nationality'), group: z.literal('traveler') }),
  z.object({
    kind: z.literal('document'),
    group: z.literal('traveler'),
    allow: z.array(z.enum(['cpf', 'passport', 'dni'])),
  }),
  // Brings the airline note ("companhias aéreas … MR/MRS") with it — the «Por que?» popover of
  // `GenderField` — so it is meant for
  // flight (`FLIGHT_FIELDS`); a non-flight product adding it needs its own note. Requiredness is
  // server-driven (`canSubmit` gates on `missingRequired`).
  z.object({ kind: z.literal('gender'), group: z.literal('traveler') }),
  // flight-only identity superset
  z.object({ kind: z.literal('embark'), group: z.literal('traveler') }), // boarding name + surname
  z.object({ kind: z.literal('phone'), group: z.literal('traveler') }), // required for flight
]);
export type CheckoutField = z.infer<typeof CheckoutFieldSchema>;

// Exhaustiveness anchor for `form_host.tsx`, which dispatches on `field.kind`
// directly (each field has a distinct prop shape — a component-keyed map would
// need `unknown` casts). Not exported: nothing else needs the list as a value:
// this array only exists to make `_assertAllFieldKindsListed` fail to compile
// if a `kind` is added to the union above without being listed here. Mirrors
// `CHAT_UI_KINDS`/`_assertAllKindsListed` in `index.ts`.
const FIELD_KINDS = [
  'allocation',
  'select',
  'observation',
  'birthdate',
  'nationality',
  'document',
  'gender',
  'embark',
  'phone',
] as const satisfies readonly CheckoutField['kind'][];
type _MissingFieldKinds = Exclude<CheckoutField['kind'], (typeof FIELD_KINDS)[number]>;
// `T extends never` fails to compile while some kind is missing. Type-only (`declare`): there is
// no value to keep alive, so nothing has to reference it.
type _AssertNoneMissing<T extends never> = T;
declare const _assertAllFieldKindsListed: _AssertNoneMissing<_MissingFieldKinds>;

// ── Prefill (2ª compra da mesma trip — spec 2026-08-28) ─────────────────────

/** Uma linha de alocação do prefill — ids apenas (sem `name`): o cliente
 *  resolve o display contra o booking form fresco, o que também invalida um
 *  id stale em vez de exibir um rótulo morto. */
export const CheckoutPrefillAllocationSchema = z.object({
  companyId: z.string().min(1),
  costCenterId: z.string().min(1),
  projectId: z.string().min(1).optional(),
});
export type CheckoutPrefillAllocation = z.infer<typeof CheckoutPrefillAllocationSchema>;

/** Um viajante do prefill, casado por `id` contra o roster ATUAL do card (não
 *  por nome/posição) — um viajante do snapshot que não está mais no roster
 *  simplesmente não encontra par e é ignorado pelo consumidor. */
export const CheckoutPrefillTravelerSchema = z.object({
  id: z.string().min(1),
  allocation: CheckoutPrefillAllocationSchema.optional(),
  edits: CheckoutTravelerEditsSchema.optional(),
});
export type CheckoutPrefillTraveler = z.infer<typeof CheckoutPrefillTravelerSchema>;

/** Prefill derivado do `CheckoutSnapshot` da compra anterior da MESMA trip
 *  (ver `checkoutPrefillFromSnapshot`). Puramente IDs/edits — nunca nomes.
 *
 *  Do snapshot só saem `travelers` (rateio + dados do viajante) e a origem.
 *  `motiveId`/`justificationId`/`observation` NÃO vêm da compra anterior
 *  (#5808): existem para o repasse do card 1click para a tela cheia
 *  (`handoffPrefill` — o que a pessoa escolheu NESTE card, inclusive o rascunho
 *  que o ✕ da tela cheia devolveu a ele) e seguem no schema para os blocos já
 *  persistidos com eles. */
export const CheckoutPrefillSchema = z.object({
  motiveId: z.string().min(1).optional(),
  justificationId: z.string().min(1).optional(),
  observation: z.string().max(300).optional(),
  travelers: z.array(CheckoutPrefillTravelerSchema).optional(),
  sourceProductType: z.string().optional(),
  capturedAt: z.string().optional(),
});
export type CheckoutPrefill = z.infer<typeof CheckoutPrefillSchema>;

// ── ChatUi block (`product_checkout` kind) ──────────────────────────────────

const ChatUiProductCheckoutSchema = z.object({
  challengeId: z.string().min(1),
  items: z.array(CheckoutItemSchema).min(1),
  // O roster do drawer. Todo produtor emite (carro, ônibus e mobilidade com o
  // solicitante sozinho); opcional porque blocos persistidos antes disso ainda
  // reabrem no histórico — o drawer então resolve o solicitante pelo form.
  travelers: z.array(TravelerRefSchema).optional(),
  requesterId: z.string().min(1).optional(),
  policyTravelerId: z.string().min(1).optional(),
  // A viagem a que esta compra se junta (#3180) — é ela que deixa o painel
  // "Sua viagem" mostrar os serviços JÁ comprados ao lado do que está sendo
  // solicitado agora. Aditivo como o `prefill`: bloco antigo (replay) segue
  // parseando sem a chave, e o cliente cai no vínculo da conversa.
  tripId: z.string().min(1).optional(),
  // Prefill da compra anterior da MESMA trip (spec 2026-08-28). Aditivo: bundle
  // velho descarta a chave (objeto interno não é .strict()) e renderiza o card
  // de hoje. IDs, não nomes — o cliente resolve display no booking form fresco.
  prefill: CheckoutPrefillSchema.optional(),
  // Documentos da viagem (voo internacional): aditivo como o `prefill`.
  travelDocs: CheckoutTravelDocsSchema.optional(),
});
export type ChatUiProductCheckout = z.infer<typeof ChatUiProductCheckoutSchema>;

/** Membro `product_checkout` do `ChatUiSchema` (discriminatedUnion) — o
 *  checkout unificado (rental + hotel + flight + bus + mobility). */
export const ProductCheckoutBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('product_checkout'),
    checkout: ChatUiProductCheckoutSchema,
  })
  .strict();

/**
 * Rascunho dos campos de RESERVA do drawer (Motivo/Justificativa/Observação) —
 * devolvido pelo `onClose` e re-semeado via `initialDraft` para a MESMA cotação
 * sobreviver a fechar/reabrir (CC-4). Campos por-viajante ficam de fora: eles
 * re-seedam do form corporativo refetchado, que é a fonte de verdade.
 */
export interface CheckoutBookingDraft {
  motiveId?: string;
  justificationId?: string;
  observation?: string;
}

/** Selection do CTA "Ver outras opções" do card (despachada como action `interaction`
 *  com o challengeId do checkout): gesto estruturado que os map-guards dos
 *  domínios interceptam ANTES do LLM. Estrita, com `action` literal fechado que
 *  não colide com nenhuma outra selection (planner: search/requery/search_multi;
 *  fares: enums próprios; confirm_prompt: confirm/deny). */
export const ProductCheckoutOtherOptionsSelectionSchema = z
  .object({ action: z.literal('other_options') })
  .strict();
